/**
 * Google Analytics 4 Service
 *
 * Provides event tracking for:
 * - Page views (automatic with SPA routing)
 * - CTA clicks (Get Started, Sign Up, Pricing)
 * - File uploads (upload_start, upload_complete, upload_error)
 * - Analysis runs (analysis_start, analysis_complete)
 * - Export actions (export_csv, export_excel, export_pdf)
 * - Form submissions
 * - Scroll depth (25%, 50%, 75%, 100%)
 */

// Extend window to include gtag
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js',
      targetId: string | Date,
      params?: Record<string, unknown>
    ) => void;
    dataLayer: unknown[];
  }
}

import { createLogger } from './loggingService';

const logger = createLogger('AnalyticsService');

// Get measurement ID from environment variable
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

let isInitialized = false;

// Check if analytics is enabled
export const isAnalyticsEnabled = (): boolean => {
  return Boolean(isInitialized && typeof window !== 'undefined' && window.gtag);
};

// Load Google Analytics script dynamically
const loadGAScript = (measurementId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.gtag) {
      resolve();
      return;
    }

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };

    // Create and load the script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

    script.onload = () => {
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false, // We'll send page views manually for SPA
      });
      resolve();
    };

    script.onerror = () => {
      reject(new Error('Failed to load Google Analytics script'));
    };

    document.head.appendChild(script);
  });
};

// Initialize GA4 (called once on app load)
export const initializeAnalytics = async (): Promise<void> => {
  if (isInitialized) return;

  if (!GA_MEASUREMENT_ID) {
    logger.debug('VITE_GA_MEASUREMENT_ID not set, analytics disabled');
    return;
  }

  // Don't initialize in development by default (unless explicitly set)
  if (import.meta.env.DEV && !import.meta.env.VITE_ENABLE_ANALYTICS_DEV) {
    logger.debug('Disabled in development mode');
    return;
  }

  try {
    await loadGAScript(GA_MEASUREMENT_ID);
    isInitialized = true;
    logger.info('Initialized', { measurementId: GA_MEASUREMENT_ID });
  } catch (error) {
    logger.error('Failed to initialize', { error: error instanceof Error ? error.message : String(error) });
  }
};

/**
 * Track page view
 * Called automatically when route changes in SPA
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('config', GA_MEASUREMENT_ID!, {
    page_path: path,
    page_title: title || document.title,
  });
};

// ============================================
// CTA Click Events
// ============================================

export type CTAType =
  | 'get_started'
  | 'sign_up'
  | 'start_free_trial'
  | 'pricing_view'
  | 'pricing_select_tier'
  | 'login'
  | 'open_optimizer';

export const trackCTAClick = (ctaType: CTAType, metadata?: Record<string, unknown>): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'cta_click', {
    cta_type: ctaType,
    ...metadata,
  });
};

// ============================================
// File Upload Events
// ============================================

export type FileUploadType = 'portfolio' | 'margin' | 'verification';

export const trackFileUploadStart = (fileType: FileUploadType, fileName: string): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'upload_start', {
    file_type: fileType,
    file_name: fileName,
  });
};

export const trackFileUploadComplete = (
  fileType: FileUploadType,
  fileName: string,
  asinsCount?: number
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'upload_complete', {
    file_type: fileType,
    file_name: fileName,
    asins_count: asinsCount,
  });
};

export const trackFileUploadError = (
  fileType: FileUploadType,
  fileName: string,
  errorMessage: string
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'upload_error', {
    file_type: fileType,
    file_name: fileName,
    error_message: errorMessage.slice(0, 100), // Limit error message length
  });
};

// ============================================
// Analysis Events
// ============================================

export const trackAnalysisStart = (asinsCount: number): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'analysis_start', {
    asins_count: asinsCount,
  });
};

export const trackAnalysisComplete = (
  asinsCount: number,
  durationMs: number,
  projectedProfitDelta?: number
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'analysis_complete', {
    asins_count: asinsCount,
    duration_ms: durationMs,
    projected_profit_delta: projectedProfitDelta,
  });
};

export const trackAnalysisError = (errorMessage: string): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'analysis_error', {
    error_message: errorMessage.slice(0, 100),
  });
};

// ============================================
// Export Events
// ============================================

export type ExportFormat = 'csv' | 'excel' | 'pdf';

export const trackExport = (format: ExportFormat, itemCount?: number): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', `export_${format}`, {
    item_count: itemCount,
  });
};

// ============================================
// Form Events
// ============================================

export type FormType = 'login' | 'signup' | 'contact' | 'feedback' | 'margin_update';

export const trackFormStart = (formType: FormType): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'form_start', {
    form_type: formType,
  });
};

export const trackFormSubmit = (formType: FormType, success: boolean): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'form_submit', {
    form_type: formType,
    success,
  });
};

// ============================================
// Scroll Depth Events
// ============================================

const scrollDepthsTracked = new Set<number>();

export const trackScrollDepth = (depth: 25 | 50 | 75 | 100): void => {
  if (!isAnalyticsEnabled()) return;

  // Only track each depth once per page
  if (scrollDepthsTracked.has(depth)) return;
  scrollDepthsTracked.add(depth);

  window.gtag('event', 'scroll_depth', {
    depth_percentage: depth,
  });
};

export const resetScrollDepthTracking = (): void => {
  scrollDepthsTracked.clear();
};

// ============================================
// User Engagement Events
// ============================================

export const trackFeatureUsed = (featureName: string, metadata?: Record<string, unknown>): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'feature_used', {
    feature_name: featureName,
    ...metadata,
  });
};

export const trackTabChange = (tabName: string): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'tab_change', {
    tab_name: tabName,
  });
};

// ============================================
// Error Tracking
// ============================================

export const trackError = (
  errorType: string,
  errorMessage: string,
  componentName?: string
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'error', {
    error_type: errorType,
    error_message: errorMessage.slice(0, 100),
    component_name: componentName,
  });
};

// ============================================
// Chatbot Events
// ============================================

export const trackChatbotOpen = (): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'chatbot_open', {});
};

export const trackChatbotMessage = (isUserMessage: boolean): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'chatbot_message', {
    is_user_message: isUserMessage,
  });
};

// ============================================
// Subscription/Pricing Events
// ============================================

export const trackPricingView = (tier?: string): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'view_pricing', {
    tier,
  });
};

export const trackCheckoutStart = (tier: string, amount?: number): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'begin_checkout', {
    tier,
    value: amount,
    currency: 'USD',
  });
};

export const trackPurchaseComplete = (tier: string, amount: number): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'purchase', {
    tier,
    value: amount,
    currency: 'USD',
  });
};

// ============================================
// Sandbox Mode Events
// ============================================

export type SandboxAction = 'enter' | 'exit' | 'save' | 'apply' | 'discard';

export const trackSandboxMode = (
  action: SandboxAction,
  feature: 'pricing' | 'promotions' | 'reporting',
  metadata?: Record<string, unknown>
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'sandbox_mode', {
    action,
    feature,
    ...metadata,
  });
};

// ============================================
// Pricing Optimizer Events
// ============================================

export const trackPricingAnalysis = (
  action: 'elasticity_calculated' | 'optimal_price_generated' | 'recommendation_applied',
  metadata?: Record<string, unknown>
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'pricing_analysis', {
    action,
    ...metadata,
  });
};

export const trackPricingImport = (
  rowCount: number,
  success: boolean,
  errorMessage?: string
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'pricing_import', {
    row_count: rowCount,
    success,
    error_message: errorMessage?.slice(0, 100),
  });
};

// ============================================
// Promotions Module Events
// ============================================

export const trackPromotionAnalysis = (
  action: 'roi_calculated' | 'promo_scheduled' | 'conflict_detected' | 'recommendation_generated',
  metadata?: Record<string, unknown>
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'promotion_analysis', {
    action,
    ...metadata,
  });
};

export const trackPromotionImport = (
  rowCount: number,
  success: boolean,
  errorMessage?: string
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'promotion_import', {
    row_count: rowCount,
    success,
    error_message: errorMessage?.slice(0, 100),
  });
};

// ============================================
// P&L Reporting Events
// ============================================

export const trackReportingAction = (
  action: 'report_generated' | 'drill_down' | 'period_changed' | 'metric_toggled' | 'template_used',
  metadata?: Record<string, unknown>
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'reporting_action', {
    action,
    ...metadata,
  });
};

export const trackReportExport = (
  format: ExportFormat,
  reportType: 'pl_summary' | 'detailed' | 'custom',
  rowCount?: number
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'report_export', {
    format,
    report_type: reportType,
    row_count: rowCount,
  });
};

// ============================================
// Filter & Chart Interaction Events
// ============================================

export const trackFilterUsage = (
  feature: 'pricing' | 'promotions' | 'reporting' | 'inventory',
  filterType: string,
  filterValue?: string
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'filter_usage', {
    feature,
    filter_type: filterType,
    filter_value: filterValue,
  });
};

export const trackChartInteraction = (
  feature: 'pricing' | 'promotions' | 'reporting' | 'optimizer',
  interactionType: 'hover' | 'click' | 'zoom' | 'pan' | 'export',
  chartType?: string
): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'chart_interaction', {
    feature,
    interaction_type: interactionType,
    chart_type: chartType,
  });
};

// ============================================
// Help Center Events
// ============================================

export const trackHelpArticleView = (articleId: string, category: string): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'help_article_view', {
    article_id: articleId,
    category,
  });
};

export const trackHelpFeedback = (articleId: string, helpful: boolean): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'help_feedback', {
    article_id: articleId,
    helpful,
  });
};

export const trackHelpSearch = (query: string, resultsCount: number): void => {
  if (!isAnalyticsEnabled()) return;

  window.gtag('event', 'help_search', {
    search_query: query.slice(0, 50),
    results_count: resultsCount,
  });
};
